import React, { useState } from "react";
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from "lucide-react";
import { Toast as ToastType, ToastVariant } from "../contexts/ToastContext";
import { Button } from "@core/button";

const variants: Record<
  ToastVariant,
  { icon: React.ReactNode; className: string }
> = {
  success: {
    icon: <CheckCircle className="w-5 h-5" />,
    className:
      "bg-green-50 dark:bg-green-900 text-green-800 dark:text-green-100",
  },
  error: {
    icon: <AlertCircle className="w-5 h-5" />,
    className: "bg-red-50 dark:bg-red-900 text-red-800 dark:text-red-100",
  },
  warning: {
    icon: <AlertTriangle className="w-5 h-5" />,
    className:
      "bg-yellow-50 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100",
  },
  info: {
    icon: <Info className="w-5 h-5" />,
    className: "bg-blue-50 dark:bg-blue-900 text-blue-800 dark:text-blue-100",
  },
};

interface ToastProps extends ToastType {
  onDismiss: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({
  id,
  title,
  description,
  variant,
  onDismiss,
}) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(id);
    }, 200);
  };

  return (
    <div
      className={`
        max-w-md w-full pointer-events-auto overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5
        transform transition-all duration-200 ease-in-out
        ${variants[variant].className}
        ${isExiting ? "opacity-0 translate-x-full" : "opacity-100"}
      `}
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">{variants[variant].icon}</div>
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium">{title}</p>
            {description && (
              <p className="mt-1 text-sm opacity-90">{description}</p>
            )}
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <Button
              rounded="md"
              bgColor=""
              textColor="text-gray-400 hover:text-gray-500"
              className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={handleDismiss}
            >
              <span className="sr-only">Close</span>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
